<template>
  <div class="cross-sell-picker">
    <!-- Selected products, always visible on top so they can be unchecked -->
    <div class="cross-sell-selected">
      <label class="form-label">
        {{ $t('crossSell.selectedTitle') }}
        <span class="cross-sell-count">{{ selectedItems.length }}/{{ maxItems }}</span>
      </label>
      <div v-if="selectedItems.length" class="cross-sell-chips">
        <span
          v-for="item in selectedItems"
          :key="item.id"
          class="cross-sell-chip"
        >
          {{ item.name }}
          <a class="cross-sell-chip-remove" @click.prevent="removeItem(item.id)">&times;</a>
        </span>
      </div>
      <p v-else class="cross-sell-empty">{{ $t('crossSell.noneSelected') }}</p>
    </div>

    <!-- Filters: search + category -->
    <div class="cross-sell-filters row">
      <div class="col-md-8">
        <input
          type="text"
          class="form-control"
          :placeholder="$t('crossSell.searchPlaceholder')"
          v-model="search"
        >
      </div>
      <div class="col-md-4">
        <select class="form-control" v-model="categoryId">
          <option :value="null">{{ $t('crossSell.allCategories') }}</option>
          <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
        </select>
      </div>
    </div>

    <!-- Results -->
    <div class="cross-sell-results">
      <div v-if="loading" class="cross-sell-hint">{{ $t('crossSell.loading') }}</div>
      <div v-else-if="!results.length" class="cross-sell-hint">{{ $t('crossSell.noResults') }}</div>
      <div
        v-for="product in results"
        v-else
        :key="product.id"
        class="cross-sell-row"
        :class="{ 'is-checked': isChecked(product.id) }"
        @click="toggle(product)"
      >
        <input
          type="checkbox"
          :checked="isChecked(product.id)"
          class="cross-sell-row-check"
          style="pointer-events: none;"
        >
        <span class="cross-sell-row-name">{{ product.name }}</span>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="!loading && total > 0" class="cross-sell-pagination">
      <button
        type="button"
        class="btn btn-default btn-sm cross-sell-page-btn"
        :disabled="page <= 1"
        @click="goToPage(page - 1)"
      >
        &laquo;
      </button>
      <span class="cross-sell-page-info">
        {{ $t('crossSell.pageInfo', { page: page, pages: lastPage, total: total }) }}
      </span>
      <button
        type="button"
        class="btn btn-default btn-sm cross-sell-page-btn"
        :disabled="page >= lastPage"
        @click="goToPage(page + 1)"
      >
        &raquo;
      </button>
    </div>
  </div>
</template>

<script>
import swal from 'sweetalert2'

export default {
  name: 'ProductCrossSellPicker',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    companyId: {
      type: [Number, String],
      required: true
    },
    locationId: {
      type: [Number, String],
      required: true
    },
    maxItems: {
      type: Number,
      default: 10
    }
  },
  data () {
    return {
      search: '',
      categoryId: null,
      categories: [],
      results: [],
      selectedItems: [],
      loading: false,
      searchTimeout: null,
      page: 1,
      lastPage: 1,
      total: 0
    }
  },
  computed: {
    selectedIds () {
      return this.selectedItems.map(item => item.id)
    }
  },
  watch: {
    search () {
      this.debouncedSearch()
    },
    categoryId () {
      this.page = 1
      this.searchProducts()
    }
  },
  created () {
    this.loadCategories()
    this.hydrateSelected()
    this.searchProducts()
  },
  methods: {
    baseParams (extra = {}) {
      return Object.assign({
        token: this.$session.get('token'),
        company_id: this.companyId,
        location_id: this.locationId
      }, extra)
    },
    loadCategories () {
      this.apiRequest('/location-cross-sell/categories', 'GET', this.baseParams(), response => {
        if (response.status) {
          this.categories = response.data
        }
      })
    },
    hydrateSelected () {
      if (!this.value || !this.value.length) {
        this.selectedItems = []
        return
      }

      const params = this.baseParams({ ids: this.value.join(',') })
      this.apiRequest('/location-cross-sell/products-search', 'GET', params, response => {
        if (response.status) {
          // Preserve the saved order.
          const map = new Map(response.data.map(p => [p.id, p]))
          this.selectedItems = this.value
            .map(id => map.get(id) || map.get(Number(id)))
            .filter(Boolean)
        }
      })
    },
    debouncedSearch () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.page = 1
        this.searchProducts()
      }, 300)
    },
    goToPage (page) {
      if (page < 1 || page > this.lastPage || page === this.page) {
        return
      }
      this.page = page
      this.searchProducts()
    },
    searchProducts () {
      const params = this.baseParams({
        q: this.search,
        category_id: this.categoryId || '',
        page: this.page
      })

      this.loading = true
      this.apiRequest('/location-cross-sell/products-search', 'GET', params, response => {
        if (response.status) {
          this.results = response.data
          if (response.pagination) {
            this.total = response.pagination.total
            this.lastPage = response.pagination.last_page
            this.page = response.pagination.current_page
          }
        } else {
          this.results = []
          this.total = 0
          this.lastPage = 1
        }
        this.loading = false
      })
    },
    isChecked (id) {
      return this.selectedIds.indexOf(id) !== -1
    },
    toggle (product) {
      if (this.isChecked(product.id)) {
        this.removeItem(product.id)
        return
      }

      if (this.selectedItems.length >= this.maxItems) {
        swal('', this.$t('crossSell.maxReached', { max: this.maxItems }), 'warning')
        return
      }

      this.selectedItems.push({
        id: product.id,
        name: product.name,
        category_id: product.category_id
      })
      this.emitChange()
    },
    removeItem (id) {
      this.selectedItems = this.selectedItems.filter(item => item.id !== id)
      this.emitChange()
    },
    emitChange () {
      this.$emit('input', this.selectedIds)
    }
  }
}
</script>

<style scoped>
.cross-sell-picker {
  width: 100%;
}
.cross-sell-selected {
  margin-bottom: 12px;
}
.cross-sell-count {
  font-weight: 600;
  color: #66615b;
  margin-left: 6px;
}
.cross-sell-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}
.cross-sell-chip {
  display: inline-flex;
  align-items: center;
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  color: #2e7d32;
  border-radius: 16px;
  padding: 4px 10px;
  font-size: 13px;
}
.cross-sell-chip-remove {
  margin-left: 8px;
  cursor: pointer;
  font-weight: 700;
  color: #2e7d32;
  line-height: 1;
}
.cross-sell-chip-remove:hover {
  color: #b71c1c;
}
.cross-sell-empty {
  color: #9a9a9a;
  font-style: italic;
  margin-top: 6px;
}
.cross-sell-filters {
  margin-bottom: 10px;
}
.cross-sell-results {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
}
.cross-sell-hint {
  padding: 12px;
  color: #9a9a9a;
  text-align: center;
}
.cross-sell-row {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}
.cross-sell-row:last-child {
  border-bottom: none;
}
.cross-sell-row:hover {
  background: #f7f7f7;
}
.cross-sell-row.is-checked {
  background: #e8f5e9;
}
.cross-sell-row-check {
  margin-right: 10px;
}
.cross-sell-row-name {
  font-size: 14px;
}
.cross-sell-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 10px;
}
.cross-sell-page-btn {
  min-width: 34px;
}
.cross-sell-page-info {
  font-size: 13px;
  color: #66615b;
}
</style>



// WEBPACK FOOTER //
// src/components/UIComponents/Inputs/ProductCrossSellPicker.vue